/**
 * aviImport.js — дверь для видео AVI в общий путь загрузки. [12.0.41]
 *
 * Зовётся из fileOps.handleFiles ПЕРВЫМ делом, до проверки подписи DICM:
 * та проверка выбрасывала AVI молча (а при одном файле — даже без тоста,
 * он появляется только от четырёх пропущенных). Здесь каждый .avi
 * превращается в многокадровый УЗИ-DICOM (io/aviCore.js), и дальше его везёт
 * обычный путь — ровно как эхо, выгруженное аппаратом в DICOM.
 *
 * СМОТРИМ ТОЛЬКО НА ИМЯ. Файлы без расширения .avi не читаются вовсе:
 * лишнее чтение каждого файла на телефоне — это минуты на пяти тысячах
 * срезов (см. [10.5.7] в fileOps). Подпись RIFF…AVI проверяется уже у
 * отобранных.
 *
 * Не открылось — врач получает причину словами (кодек, обрезанный файл), а
 * не чёрное окно. Файл при этом убирается из списка: иначе следом пришёл бы
 * второй, непонятный тост «не-DICOM».
 */

import { этоAVI, разобратьAVI, собратьDICOM } from './aviCore.js';
// [12.0.45] Cinepak («cvid») — архивные клипы владельца; браузер его не знает.
import { декодерCinepak } from './cinepak.js';
// [12.0.42] Масштаб со шкалы глубины первого кадра — см. core/echoSimpson.js.
import { шкалаГлубины } from '../core/echoSimpson.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

const ПО_ИМЕНИ = /\.avi$/i;

const запятая = (x, знаков) => x.toFixed(знаков).replace('.', ',');

/**
 * [12.0.42] Масштаб со шкалы глубины на ПЕРВОМ кадре AVI.
 *
 * Кадр распаковывает сам браузер (тот же JPEG, что ляжет в DICOM), яркость —
 * среднее трёх каналов, как и в окне ФВ по Симпсону: одно правило в двух
 * местах дало бы разные числа. Нет шкалы, нет canvas, любой сбой — null, и
 * PixelSpacing не пишется: выдуманный масштаб хуже отсутствующего.
 *
 * @returns {Promise<{ммНаПиксель:number, пояснение:string, шкала:object}|null>}
 */
/** Яркость кадра RGB — среднее трёх каналов (то же правило, что ниже для JPEG). */
function серыйИзRGB(rgb, w, h) {
  const серый = new Uint8Array(w * h);
  for (let i = 0, j = 0; i < серый.length; i++, j += 3) серый[i] = ((rgb[j] + rgb[j + 1] + rgb[j + 2]) / 3) | 0;
  return серый;
}

function масштабПоСерому(серый, w, h, имя) {
  const ш = шкалаГлубины(серый, w, h);
  if (!ш.ок) { log.info(`[AVI] ${имя}: ${ш.причина}`); return null; }
  const пояснение = `Масштаб ${запятая(ш.ммНаПиксель, 4)} мм/пикс взят со шкалы глубины на кадре: `
    + `${ш.смМеждуДлинными} см = ${Math.round(ш.пикселейМеждуДлинными)} пикс, штрихов ${ш.штрихов}. `
    + 'Допущение: по ширине масштаб тот же, что по глубине.';
  return { ммНаПиксель: ш.ммНаПиксель, пояснение, шкала: ш };
}

/**
 * [12.0.45] Распаковать все кадры Cinepak. Битый кадр в середине — не повод
 * терять клип: берётся то, что распаковалось до него, и это сказано.
 */
function кадрыCinepak(bytes, разбор, имя) {
  const д = декодерCinepak(разбор.ширина, разбор.высота);
  const кадры = [];
  for (let i = 0; i < разбор.кадры.length; i++) {
    const к = разбор.кадры[i];
    try { кадры.push(д.кадр(bytes, к.смещение, к.длина)); } catch (e) {
      if (!кадры.length) throw new Error(`Cinepak: первый кадр не распакован — ${e?.message || e}`);
      разбор.замечания.push(`кадр ${i + 1} не распакован (${e?.message || e}) — взяты первые ${кадры.length}`);
      log.warn(`[AVI] ${имя}: кадр ${i + 1} Cinepak не распакован`, e?.message);
      break;
    }
  }
  return кадры;
}

async function масштабСоШкалы(bytes, разбор, имя, кадрыRGB = null) {
  try {
    if (кадрыRGB) return масштабПоСерому(серыйИзRGB(кадрыRGB[0], разбор.ширина, разбор.высота), разбор.ширина, разбор.высота, имя);
    if (typeof createImageBitmap !== 'function') return null;
    const к = разбор.кадры[0];
    const bmp = await createImageBitmap(new Blob([bytes.subarray(к.смещение, к.смещение + к.длина)], { type: 'image/jpeg' }));
    const w = bmp.width, h = bmp.height;
    const холст = (typeof OffscreenCanvas !== 'undefined')
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });
    const cx = холст.getContext('2d', { willReadFrequently: true });
    cx.drawImage(bmp, 0, 0);
    try { bmp.close?.(); } catch (e) { /* */ }
    const rgba = cx.getImageData(0, 0, w, h).data;
    const серый = new Uint8Array(w * h);
    for (let i = 0, j = 0; i < серый.length; i++, j += 4) серый[i] = ((rgba[j] + rgba[j + 1] + rgba[j + 2]) / 3) | 0;
    return масштабПоСерому(серый, w, h, имя);
  } catch (e) {
    log.warn(`[AVI] ${имя}: шкалу глубины прочитать не удалось — ${e?.message || e}`);
    return null;
  }
}

/** 1 кадр, 24 кадра, 213 кадров. */
function кадров(n) {
  const д = n % 100, е = n % 10;
  const слово = (д >= 11 && д <= 14) ? 'кадров' : (е === 1 ? 'кадр' : (е >= 2 && е <= 4 ? 'кадра' : 'кадров'));
  return `${n} ${слово}`;
}

/**
 * @param {File[]|FileList} files
 * @param {(номер:number, всего:number, имя:string) => void} [сказать] ход работы на экран
 * @returns {Promise<File[]>} тот же список, где AVI заменены на DICOM, а неоткрываемые AVI убраны
 */
export async function заменитьAVIнаDICOM(files, сказать) {
  const список = Array.from(files || []);
  const видео = список.filter((f) => f && ПО_ИМЕНИ.test(f.name || ''));
  if (!видео.length) return список;

  // Всё, что открыто одним разом, — одно исследование: в AVI нет своего
  // UID, а клипы одного осмотра врач выбирает вместе (папкой или пачкой).
  const ключИсследования = видео.map((f) => `${f.name}|${f.size}`).sort().join(';');
  // Номер серии — по порядку имён: у Mindray имя начинается со времени съёмки.
  const поПорядку = [...видео].sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const замена = new Map();
  const отказ = new Set();
  let кадровВсего = 0;
  const масштабы = [];   // [12.0.42] найденные на шкале, по одному на открытый файл
  const неполные = [];   // [12.0.44] файлы, где целых кадров меньше заявленного
  for (let i = 0; i < поПорядку.length; i++) {
    const f = поПорядку[i];
    try { сказать?.(i + 1, поПорядку.length, f.name); } catch (e) { /* экран — не повод ломать загрузку */ }
    try {
      const bytes = new Uint8Array(await f.arrayBuffer());
      if (!этоAVI(bytes)) throw new Error('расширение .avi, но внутри не AVI');
      const разбор = разобратьAVI(bytes);
      const кадрыRGB = разбор.сжатие === 'cinepak' ? кадрыCinepak(bytes, разбор, f.name) : null;
      const м = await масштабСоШкалы(bytes, разбор, f.name, кадрыRGB);
      масштабы.push(м);
      const { части, байт } = собратьDICOM(разбор, bytes, {
        имяФайла: f.name, размер: f.size, изменён: f.lastModified,
        ключИсследования, номерСерии: i + 1,
        ммНаПиксель: м?.ммНаПиксель, пояснениеМасштаба: м?.пояснение,
        ...(кадрыRGB ? { кадрыRGB } : {}),
      });
      if (м) log.info(`[AVI] ${f.name}: ${м.пояснение}`);
      замена.set(f, new File(части, f.name + '.dcm', { type: 'application/dicom', lastModified: f.lastModified }));
      кадровВсего += кадрыRGB ? кадрыRGB.length : разбор.кадры.length;
      if (разбор.неполон) неполные.push({ имя: f.name, ...разбор.неполон });
      const кс = Math.round(разбор.кадровВСекунду * 10) / 10;
      log.info(`[AVI] ${f.name}: ${разбор.кодек}, ${разбор.ширина}×${разбор.высота}, ${кадров(разбор.кадры.length)}`
        + ` (в заголовке ${разбор.заявленоКадров}), ${кс} кадров/с → УЗИ-DICOM ${(байт / 1048576).toFixed(1)} МБ`);
      if (разбор.замечания.length) log.warn(`[AVI] ${f.name}: ${разбор.замечания.join('; ')}`);
    } catch (e) {
      отказ.add(f);
      log.warn(`[AVI] ${f.name}: не открыт — ${e?.message || e}`);
      try { showToast(`❌ ${f.name}: ${e?.message || e}`, 'error', 9000); } catch (e2) { /* */ }
    }
  }

  if (замена.size) {
    // [12.0.42] О масштабе — правду. В 12.0.41 здесь стояло «размеры будут в
    // пикселях»; по исходникам Cornerstone 5.6.3 линейка при этом подписывает
    // пиксели как «мм»: провайдер программы подставляет шаг 1, когда Pixel
    // Spacing нет, а флаг «есть масштаб» берётся из шага > 0 (buildMetadata,
    // calibrateImagePlaneModule). На живой программе не проверено. Теперь
    // масштаб берётся со шкалы глубины, а если её нет — сказано, что «мм» у
    // линейки условные.
    const найдены = масштабы.filter(Boolean);
    let оМасштабе;
    if (найдены.length === масштабы.length) {
      const м = найдены[0];
      оМасштабе = `Масштаб со шкалы глубины: ${запятая(м.ммНаПиксель, 3)} мм/пикс `
        + `(${м.шкала.смМеждуДлинными} см = ${Math.round(м.шкала.пикселейМеждуДлинными)} пикс) — сверьте по шкале.`;
    } else if (найдены.length) {
      оМасштабе = `Шкала глубины найдена у ${найдены.length} из ${масштабы.length}; где не найдена — масштаба нет, «мм» у линейки условные (это пиксели).`;
    } else {
      // [12.0.45] Куда идти за масштабом: окно эхо, «По меткам линейки».
      оМасштабе = 'Шкала глубины на кадре не найдена — масштаба нет: «мм» у линейки условные (это пиксели). '
        + 'Для эхо масштаб задаётся в окне ФВ/камер: «По меткам линейки» — две метки и сантиметры между ними.';
    }
    try {
      showToast(`🎞 Видео AVI: ${замена.size} шт., ${кадров(кадровВсего)} — открыто как УЗИ-кино. ${оМасштабе}`,
        найдены.length === масштабы.length ? 'info' : 'warning', 8000);
    } catch (e) { /* */ }
    // [12.0.44] Неполный или повреждённый файл — отдельной плашкой, вслух.
    for (const н of неполные) {
      try {
        showToast(`⚠️ ${н.имя}: целых кадров ${н.найдено} из ${н.заявлено} заявленных `
          + `(${запятая(н.секунд, 1)} с из ${запятая(н.секундЗаявлено, 1)} с) — файл неполон или повреждён. `
          + 'Показано то, что цело; выгрузите клип с аппарата заново.', 'warning', 12000);
      } catch (e) { /* */ }
    }
  }
  return список.filter((f) => !отказ.has(f)).map((f) => замена.get(f) || f);
}

export default { заменитьAVIнаDICOM };
